import React from "react";
import { notification } from "antd";
import { Toast } from "antd-mobile";
import { isMobile, isTablet, isWeb } from "./layout";
import { AppNotificationType } from "../constants/appNotification";
import type {
  IAppNotification,
  WebNotificationProps,
} from "../types/app-notification";
import {
  BellOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

// Track active notifications to prevent duplicates
export const activeNotifications = new Set<string>();

// Create a unique key for each notification based on its content
const createNotificationKey = (
  title: string,
  message?: string,
  type?: string
) => {
  return `${type || "default"}:${title}:${message || ""}`;
};

export const appNotification: IAppNotification = ({
  nTitle,
  nMessage,
  nType,
  ...props
}) => {
  // Create a unique key for this notification
  const notificationKey = createNotificationKey(nTitle, nMessage, nType);

  // Check if this notification is already showing
  if (activeNotifications.has(notificationKey)) {
    return;
  }

  // Mark as active
  activeNotifications.add(notificationKey);

  if (isWeb() || isTablet()) {
    return webNotification({
      message: nTitle,
      description: nMessage || "",
      notificationType: nType,
      duration: 3, // Default 3 seconds (can be overridden by props)
      onClose: () => {
        // Remove from active set when notification closes
        activeNotifications.delete(notificationKey);
        // Call original onClose if provided
        if (props.onClose) {
          props.onClose();
        }
      },
      ...props, // Spread props after to allow duration override
    });
  }
  if (isMobile()) {
    const icon = () => {
      switch (nType) {
        case AppNotificationType.SUCCESS:
          return <CheckCircleOutlined />;
        case AppNotificationType.ERROR:
          return <ExclamationCircleOutlined />;
        case AppNotificationType.LOADING:
          return <ReloadOutlined />;
        default:
          return <BellOutlined />;
      }
    };

    const handler = Toast.show({
      content: (
        <div
          data-test-id="bar"
          className={
            nType === AppNotificationType.SUCCESS
              ? "successNotificationToast"
              : "notificationToast"
          }
        >
          {icon()} {nTitle}
        </div>
      ),
      position: "bottom",
      duration: 3000, // 3 seconds for mobile
    });

    // Toast auto-closes after duration, remove from tracking
    setTimeout(() => {
      activeNotifications.delete(notificationKey);
    }, 3100); // Cleanup after toast duration + 100ms buffer

    return handler;
  }
};

const webNotification = (props: WebNotificationProps) => {
  switch (props.notificationType) {
    case AppNotificationType.SUCCESS:
      return notification.success(props);
    case AppNotificationType.ERROR:
      return notification.error(props);
    case AppNotificationType.INFO:
      return notification.info(props);
    case AppNotificationType.WARNING:
      return notification.warning(props);
    default:
      return notification.open(props);
  }
};

// ToDo: notification cleaup function if required
